import React from 'react'
import { LocationEnum } from '../../types'
import { getTravelCostsFromCurrentLocation } from '../../constants/travelCosts'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { formatUnderscoreCase } from '@/utils'

interface TravelModalProps {
  toLocation: LocationEnum
  toSpot: string
  onConfirm: () => void
  onCancel: () => void
}

export const TravelModal: React.FC<TravelModalProps> = ({ toLocation, toSpot, onConfirm, onCancel }) => {
  const { gameData, incrementCurrency, changeLocation, changeLocationSpot, setGameData } = useGameplayContext()

  const travelCost = getTravelCostsFromCurrentLocation(gameData, toLocation)
  const isLocationChange = gameData.currentLocation !== toLocation
  const hasEnoughMoney = gameData.currencies.money.amount >= travelCost
  const travelIcon = isLocationChange ? '🌎' : '📍'

  const handleTravel = () => {
    incrementCurrency('money', -travelCost)
    if (isLocationChange) {
      setGameData((data) => ({
        ...data,
        currentLocation: toLocation,
        currentJob: undefined,
        currentActivity: 'rest'
      }))
    }
    changeLocationSpot(toSpot)
    onConfirm()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-blue-600 rounded-lg p-6 shadow-lg text-white max-w-md w-full">
        <h2 className="text-2xl font-bold mb-4">Travel</h2>
        <p className="mb-4">
          You decided to travel to the new surf spot{' '}
          <b>
            {toSpot} {isLocationChange ? `in ${formatUnderscoreCase(toLocation)}` : ''}
          </b>
          . Your current job will be reset.
        </p>
        <p className="mb-4">
          The cost to travel is: <b>${travelCost}</b>
        </p>
        {!hasEnoughMoney && <p className="text-red-300 mb-4">You don't have enough money for this trip!</p>}
        <div className="flex justify-end space-x-4">
          <button
            className="px-4 py-2 rounded-lg text-sm font-bold transition-colors bg-gray-500 text-white hover:bg-gray-600"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
              hasEnoughMoney
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-green-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleTravel}
            disabled={!hasEnoughMoney}
          >
            {travelIcon} Travel
          </button>
        </div>
      </div>
    </div>
  )
}
